import { parsePermissions, type FinanceAccessRole, type ReadRole, type RolePolicy } from "./access-permissions.ts";
import type { DescriptionRule } from "./description-rules.ts";
import type { ImportSubmission } from "./import-review-draft.ts";
import type { BulkPreview, BulkRequest, BulkStatus } from "./bulk-transactions.ts";
import type { SupabaseClient } from "@supabase/supabase-js";

import { refreshTransactionDerived, transactionMissingFields, type DataQualityField, type Transaction } from "./finance.ts";
import { defaultBusinessUnits, type BusinessUnit, type RecipientBrandMapping } from "./business-units.ts";
import type { FinanceBudget } from "./budgeting.ts";
import { type FinancialAccount, type FinancialCodeRule, type CategoryFinancialMapping } from "./profit-loss.ts";
import { defaultCategories, type FinanceCategory } from "./categories.ts";
import { defaultCategoryLearningSettings, type CategoryLearningSettings } from "./category-learning.ts";

export const IMPORT_BUCKET = "finance-imports";

type TransactionRow = {
  id: string;
  payment_ref: string | null;
  ft_number?: string | null;
  source_account: string | null;
  beneficiary_account: string | null;
  amount_idr: number;
  transaction_date: string | null;
  transaction_timestamp: string | null;
  subject: string;
  internal_description?: string | null;
  source_name: string | null;
  beneficiary_name: string | null;
  source_bank: string | null;
  beneficiary_bank: string | null;
  balance_idr: number | null;
  unique_code: string;
  transfer_type: string | null;
  status: "success" | "failed" | "pending" | null;
  direction: "credit" | "debit";
  category_slug: string;
  subcategory_slug?: string | null;
  category_rule_id?: string | null;
  category_rule_conflict?: boolean;
  business_unit_slug: string;
  business_unit_slugs?: string[];
  brand_candidates?: string[];
  deleted_at?: string | null;
  updated_at?: string;
  business_unit_mapping_source?: Transaction["businessUnitMappingSource"] | null;
  account_label: string | null;
  source_file: string;
  entry_method?: "import" | "manual" | null;
  fingerprint: string;
  identity_keys: string[] | null;
  financial_account_slug?: string | null;
  financial_mapping_source?: "manual" | "rule" | "category" | null;
  category_assignment_source?: Transaction["categoryAssignmentSource"] | null;
  category_history?: Transaction["categoryHistory"];
  missing_fields?: DataQualityField[] | null;
  confirmed_empty_fields?: DataQualityField[] | null;
  data_review_status?: Transaction["dataReviewStatus"] | null;
};

type ImportSourceRow = {
  id: string;
  source_filename: string;
  source_format: string;
  file_sha256: string;
  imported_by: string;
  imported_by_name: string;
  row_count: number;
  detected_row_count?: number | null;
  excluded_count?: number | null;
  inserted_count: number;
  duplicate_count: number;
  storage_bucket: string | null;
  storage_path: string | null;
  original_size_bytes: number | string | null;
  mime_type: string | null;
  original_file_uploaded_at: string | null;
  created_at: string;
};

type SourceLinkRow = {
  transaction_id: string;
  import_batch_id: string;
};

type UniqueCodeCategoryRuleRow = {
  brand_context: string;
  automatic: boolean;
  unique_code: string;
  direction: "credit" | "debit";
  category_slug: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

type FinancialAccountRow = {
  slug: string;
  name_id: string;
  name_en: string;
  section: FinancialAccount["section"];
  display_order: number;
  is_active: boolean;
  is_system: boolean;
};

type FinancialCodeRuleRow = {
  unique_code: string;
  direction: "credit" | "debit";
  financial_account_slug: string;
  business_unit_slug: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

type BusinessUnitRow = {
  slug: string;
  name: string;
  short_name: string;
  color: string;
  is_active: boolean;
  display_order?: number;
  is_system?: boolean;
};

type RecipientBrandMappingRow = {
  match_kind: "recipient" | "bank";
  brand_slugs: string[];
  id: string;
  recipient_name: string;
  normalized_name: string;
  business_unit_slug: string;
  created_at: string;
  updated_at: string;
};

type FinanceBudgetRow = {
  id: string;
  month_start: string;
  business_unit_slug: string | null;
  category_slug: string;
  amount_idr: number | string;
  note: string | null;
  created_by: string | null;
  updated_at: string;
  is_active?: boolean;
};

type FinanceCategoryRow = {
  parent_slug?: string | null;
  slug: string;
  kind: FinanceCategory["kind"];
  name_id: string;
  name_en: string;
  color: string;
  display_order: number;
  is_active: boolean;
  is_system: boolean;
};

export type AccessProfile = {
  id: string;
  displayName: string;
  email: string;
  access: FinanceAccessRole;
  status: "pending" | "active" | "removed";
  updatedAt?: string;
  legacyRole: "admin" | "finance" | "viewer";
  createdAt: string;
  reviewedAt: string | null;
  removedAt: string | null;
};

export type ImportSource = {
  id: string;
  fileName: string;
  fileFormat: string;
  fileSha256: string;
  importedBy: string;
  importedByName: string;
  rowCount: number;
  /** Only present when returned by backend; older RPC versions do not track it. */
  detectedRowCount: number | null;
  excludedCount: number | null;
  insertedCount: number;
  duplicateCount: number;
  storageBucket: string | null;
  storagePath: string | null;
  fileSize: number | null;
  mimeType: string | null;
  uploadedAt: string | null;
  createdAt: string;
};

export type UniqueCodeCategoryRule = {
  brandContext: string;
  automatic: boolean;
  uniqueCode: string;
  direction: Transaction["direction"];
  category: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ImportSourceRowDetail = {
  rowNumber: number;
  transactionId: string;
  sourcePayload: Record<string, unknown>;
};

export type SourceDeletionPreview = {
  batchCount: number;
  transactionCount: number;
  exclusiveTransactionCount: number;
  sharedTransactionCount: number;
  originalFileCount: number;
  storagePaths: string[];
};

export type SourceDeletionResult = {
  deletedBatchCount: number;
  deletedTransactions: number;
  keptTransactions: number;
  storageCleanupFailed: boolean;
};

function fromRow(row: TransactionRow): Transaction {
  const base: Transaction = {
    id: row.id,
    paymentRef: row.payment_ref || "",
    ftNumber: row.ft_number || "",
    sourceAccount: row.source_account || "",
    beneficiaryAccount: row.beneficiary_account || "",
    amount: Number(row.amount_idr),
    date: row.transaction_date || "",
    transactionTimestamp: row.transaction_timestamp || "",
    subject: row.subject || "",
    internalDescription: row.internal_description || "",
    sourceName: row.source_name || "",
    beneficiaryName: row.beneficiary_name || "",
    sourceBank: row.source_bank || "",
    beneficiaryBank: row.beneficiary_bank || "",
    balance: row.balance_idr === null ? undefined : Number(row.balance_idr),
    uniqueCode: row.unique_code,
    businessUnit: row.business_unit_slug,
    businessUnits: row.business_unit_slugs,
    brandCandidates: row.brand_candidates || [],
    deletedAt: row.deleted_at || undefined,
    updatedAt: row.updated_at,
    businessUnitMappingSource: row.business_unit_mapping_source || undefined,
    transferType: row.transfer_type || "",
    status: row.status ? row.status.toUpperCase() as Transaction["status"] : "",
    direction: row.direction.toUpperCase() as Transaction["direction"],
    category: row.category_slug,
    subcategory: row.subcategory_slug || undefined,
    categoryRuleId: row.category_rule_id || undefined,
    categoryRuleConflict: row.category_rule_conflict || false,
    categoryAssignmentSource: row.category_assignment_source || (row.category_slug === "uncategorized" ? "unassigned" : "legacy"),
    categoryHistory: row.category_history || [],
    financialAccount: row.financial_account_slug || undefined,
    financialMappingSource: row.financial_mapping_source || undefined,
    accountLabel: row.account_label || "",
    sourceFile: row.source_file,
    entryMethod: row.entry_method === "manual" ? "manual" : "import",
    sourceBatchIds: [],
    sourceFiles: [row.source_file],
    fingerprint: row.fingerprint,
    identityKeys: row.identity_keys || [],
    missingFields: row.missing_fields || [],
    confirmedEmptyFields: row.confirmed_empty_fields || [],
    dataReviewStatus: row.data_review_status || "complete",
  };
  if (!row.missing_fields || !row.data_review_status) {
    const missingFields = transactionMissingFields(base);
    return {
      ...base,
      missingFields,
      dataReviewStatus: missingFields.length ? "needs_review" : "complete",
    };
  }
  return refreshTransactionDerived(base);
}

function sourceFromRow(row: ImportSourceRow): ImportSource {
  return {
    id: row.id,
    fileName: row.source_filename,
    fileFormat: row.source_format,
    fileSha256: row.file_sha256,
    importedBy: row.imported_by,
    importedByName: row.imported_by_name,
    rowCount: Number(row.row_count),
    detectedRowCount: row.detected_row_count == null ? null : Number(row.detected_row_count),
    excludedCount: row.excluded_count == null ? null : Number(row.excluded_count),
    insertedCount: Number(row.inserted_count),
    duplicateCount: Number(row.duplicate_count),
    storageBucket: row.storage_bucket,
    storagePath: row.storage_path,
    fileSize: row.original_size_bytes === null ? null : Number(row.original_size_bytes),
    mimeType: row.mime_type,
    uploadedAt: row.original_file_uploaded_at,
    createdAt: row.created_at,
  };
}

export function applySourceLinks(
  transactions: Transaction[],
  sources: ImportSource[],
  links: SourceLinkRow[],
) {
  const sourceById = new Map(sources.map((source) => [source.id, source]));
  const linksByTransaction = new Map<string, SourceLinkRow[]>();
  for (const link of links) {
    linksByTransaction.set(link.transaction_id, [
      ...(linksByTransaction.get(link.transaction_id) || []),
      link,
    ]);
  }
  return transactions.map((transaction) => {
    const transactionLinks = linksByTransaction.get(transaction.id) || [];
    const sourceBatchIds = [...new Set(transactionLinks.map((link) => link.import_batch_id))];
    const sourceFiles = [...new Set(transactionLinks
      .map((link) => sourceById.get(link.import_batch_id)?.fileName)
      .filter((value): value is string => Boolean(value)))];
    return {
      ...transaction,
      sourceBatchIds,
      sourceFiles: sourceFiles.length ? sourceFiles : transaction.sourceFiles,
      sourceFile: sourceFiles.length ? sourceFiles.join(" · ") : transaction.sourceFile,
    };
  });
}

export async function loadRemoteSources(client: SupabaseClient) {
  const { data, error } = await client.rpc("list_import_sources");
  if (error) throw error;
  return ((data || []) as ImportSourceRow[]).map(sourceFromRow);
}

export async function loadUniqueCodeCategoryRules(client: SupabaseClient) {
  const { data, error } = await client
    .from("contextual_code_category_rules")
    .select("unique_code,direction,brand_context,automatic,category_slug,created_by,created_at,updated_at")
    .order("unique_code", { ascending: true })
    .order("direction", { ascending: true });
  if (error) throw error;
  return ((data || []) as UniqueCodeCategoryRuleRow[]).map((row) => ({
    uniqueCode: row.unique_code,
    brandContext: row.brand_context,
    automatic: row.automatic,
    direction: row.direction.toUpperCase() as Transaction["direction"],
    category: row.category_slug,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

function isMissingBrandMappingSchema(error: { code?: string; message?: string } | null) {
  return Boolean(error && (
    error.code === "42P01"
    || error.code === "42703"
    || /recipient_brand_mappings|display_order|is_system|business_unit_mapping_source/i.test(error.message || "")
  ));
}

export async function loadBusinessUnits(client: SupabaseClient): Promise<BusinessUnit[]> {
  const { data, error } = await client
    .from("business_units")
    .select("slug,name,short_name,color,is_active,display_order,is_system")
    .order("display_order", { ascending: true });
  if (isMissingBrandMappingSchema(error)) return defaultBusinessUnits;
  if (error) throw error;
  return ((data || []) as BusinessUnitRow[]).map((row) => ({
    slug: row.slug,
    name: row.name,
    shortName: row.short_name,
    color: row.color,
    active: row.is_active,
    displayOrder: Number(row.display_order ?? 500),
    system: Boolean(row.is_system),
  }));
}

function isMissingV012Schema(error: { code?: string; message?: string } | null) {
  return Boolean(error && (
    error.code === "42P01"
    || error.code === "42703"
    || error.code === "42883"
    || /category_learning_settings|category_assignment_source|missing_fields|upsert_finance_category/i.test(error.message || "")
  ));
}

export async function loadFinanceCategories(client: SupabaseClient): Promise<FinanceCategory[]> {
  const { data, error } = await client
    .from("categories")
    .select("slug,kind,name_id,name_en,color,display_order,is_active,is_system,parent_slug")
    .order("display_order", { ascending: true });
  if (isMissingV012Schema(error)) return defaultCategories;
  if (error) throw error;
  return ((data || []) as FinanceCategoryRow[]).map((row) => ({
    slug: row.slug,
    parentSlug: row.parent_slug || undefined,
    kind: row.kind,
    nameId: row.name_id,
    nameEn: row.name_en,
    color: row.color,
    displayOrder: Number(row.display_order),
    active: row.is_active,
    system: row.is_system,
  }));
}

export async function saveFinanceCategory(client: SupabaseClient, category: FinanceCategory) {
  const { error } = await client.rpc("upsert_finance_category_v0160", {
    p_parent_slug: category.parentSlug || null,
    p_slug: category.slug,
    p_kind: category.kind,
    p_name_id: category.nameId,
    p_name_en: category.nameEn,
    p_color: category.color,
    p_display_order: category.displayOrder,
    p_is_active: category.active,
  });
  if (error) throw error;
}

export async function createInlineFinanceCategory(client: SupabaseClient, name: string, direction: Transaction["direction"]) {
  const { data, error } = await client.rpc("create_finance_category_inline", { p_name: name, p_direction: direction.toLowerCase() });
  if (error) throw error;
  return String((data as { slug: string }).slug);
}

export async function loadCategoryLearningSettings(client: SupabaseClient): Promise<CategoryLearningSettings> {
  const { data, error } = await client
    .from("category_learning_settings")
    .select("mode,minimum_support,suggestion_threshold,auto_threshold")
    .eq("singleton", true)
    .maybeSingle();
  if (isMissingV012Schema(error) || !data) return defaultCategoryLearningSettings;
  if (error) throw error;
  return {
    mode: data.mode as CategoryLearningSettings["mode"],
    minimumSupport: Number(data.minimum_support),
    suggestionThreshold: Number(data.suggestion_threshold),
    autoThreshold: Number(data.auto_threshold),
  };
}

export async function saveCategoryLearningSettings(client: SupabaseClient, settings: CategoryLearningSettings) {
  const { error } = await client.rpc("update_category_learning_settings", {
    p_mode: settings.mode,
    p_minimum_support: settings.minimumSupport,
    p_suggestion_threshold: settings.suggestionThreshold,
    p_auto_threshold: settings.autoThreshold,
  });
  if (error) throw error;
}

export async function loadRecipientBrandMappings(client: SupabaseClient): Promise<RecipientBrandMapping[]> {
  const { data, error } = await client
    .from("recipient_brand_candidates")
    .select("id,recipient_name,normalized_name,match_kind,brand_slugs,created_at,updated_at")
    .order("recipient_name", { ascending: true });
  if (isMissingBrandMappingSchema(error)) return [];
  if (error) throw error;
  return ((data || []) as RecipientBrandMappingRow[]).map((row) => ({
    id: row.id,
    recipientName: row.recipient_name,
    normalizedName: row.normalized_name,
    businessUnit: row.brand_slugs[0] || "unassigned",
    businessUnits: row.brand_slugs,
    matchKind: row.match_kind,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

export async function saveBusinessUnit(client: SupabaseClient, unit: BusinessUnit) {
  const { error } = await client.rpc("upsert_business_unit_v070", {
    p_slug: unit.slug,
    p_name: unit.name,
    p_short_name: unit.shortName,
    p_color: unit.color,
    p_display_order: unit.displayOrder,
    p_is_active: unit.active,
  });
  if (error) throw error;
}

export async function saveRecipientBrandMapping(
  client: SupabaseClient,
  recipientName: string,
  businessUnit: string,
  applyHistorical = false,
  matchKind: "recipient" | "bank" = "recipient",
) {
  const { data, error } = await client.rpc("upsert_recipient_brand_candidates", {
    p_recipient_name: recipientName,
    p_brand_slugs: businessUnit.split("+").filter((slug) => slug !== "unassigned"),
    p_match_kind: matchKind,
    p_apply_historical: applyHistorical,
  });
  if (error) throw error;
  return Number((data as Record<string, unknown> | null)?.affected_count || 0);
}

export async function removeRecipientBrandMapping(
  client: SupabaseClient,
  normalizedName: string,
  resetHistorical: boolean,
  matchKind: "recipient" | "bank" = "recipient",
) {
  const { data, error } = await client.rpc("delete_recipient_brand_candidates", {
    p_normalized_name: normalizedName,
    p_match_kind: matchKind,
    p_reset_historical: resetHistorical,
  });
  if (error) throw error;
  return Number((data as Record<string, unknown> | null)?.affected_count || 0);
}

function isMissingProfitLossSchema(error: { code?: string; message?: string } | null) {
  return Boolean(error && (
    error.code === "42P01"
    || error.code === "42703"
    || /financial_(?:accounts|code_rules|account_slug|mapping_source)/i.test(error.message || "")
  ));
}

function isMissingV080Schema(error: { code?: string; message?: string } | null) {
  return Boolean(error && (
    error.code === "42P01"
    || error.code === "42703"
    || error.code === "42883"
    || /monthly_budgets|internal_description|set_finance_access_role|upsert_monthly_budget/i.test(error.message || "")
  ));
}

export async function loadFinanceBudgets(client: SupabaseClient): Promise<FinanceBudget[]> {
  const { data, error } = await client
    .from("monthly_budgets")
    .select("id,month_start,business_unit_slug,category_slug,amount_idr,note,created_by,updated_at,is_active")
    .eq("is_active", true)
    .order("month_start", { ascending: false })
    .order("category_slug", { ascending: true });
  if (isMissingV080Schema(error)) return [];
  if (error) throw error;
  return ((data || []) as FinanceBudgetRow[]).map((row) => ({
    id: row.id,
    month: row.month_start,
    businessUnit: row.business_unit_slug,
    category: row.category_slug,
    amount: Number(row.amount_idr),
    note: row.note || "",
    createdBy: row.created_by,
    updatedAt: row.updated_at,
  }));
}

export async function saveFinanceBudget(client: SupabaseClient, budget: FinanceBudget) {
  const { data, error } = await client.rpc("upsert_monthly_budget", {
    p_month_start: budget.month,
    p_business_unit_slug: budget.businessUnit || null,
    p_category_slug: budget.category,
    p_amount_idr: Math.max(0, Math.trunc(budget.amount)),
    p_note: budget.note?.trim() || null,
  });
  if (error) throw error;
  return data as Record<string, unknown> | null;
}

export async function removeFinanceBudget(client: SupabaseClient, budget: Pick<FinanceBudget, "month" | "businessUnit" | "category">) {
  const { error } = await client.rpc("delete_monthly_budget", {
    p_month_start: budget.month,
    p_business_unit_slug: budget.businessUnit || null,
    p_category_slug: budget.category,
  });
  if (error) throw error;
}

export async function loadAccessProfiles(client: SupabaseClient): Promise<AccessProfile[]> {
  const { data, error } = await client
    .from("profiles")
    .select("id,email,display_name,role,finance_role,updated_at,access_status,created_at,access_reviewed_at,removed_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map((row) => ({
    id: String(row.id),
    displayName: String(row.display_name || "Tanpa nama"),
    email: String(row.email || ""),
    access: row.role === "admin" ? "owner" as const : (row.finance_role || "user") as Exclude<FinanceAccessRole,"owner">,
    status: row.access_status as AccessProfile["status"],
    legacyRole: row.role as AccessProfile["legacyRole"],
    createdAt: String(row.created_at),
    updatedAt: String(row.updated_at),
    reviewedAt: row.access_reviewed_at ? String(row.access_reviewed_at) : null,
    removedAt: row.removed_at ? String(row.removed_at) : null,
  }));
}

export async function saveProfileAccess(client: SupabaseClient, profileId: string, access: AccessProfile["access"], expectedUpdatedAt?: string) {
  const { error } = await client.rpc("set_finance_role_v0197", {
    p_profile_id: profileId,
    p_access_level: access,
    p_expected_updated_at: expectedUpdatedAt || null,
  });
  if (error) throw error;
}

export async function reviewProfileAccess(
  client: SupabaseClient,
  profileId: string,
  decision: "approve" | "reject" | "restore",
) {
  const { error } = await client.rpc("review_finance_access", {
    p_profile_id: profileId,
    p_decision: decision,
  });
  if (error) throw error;
}

export async function removeProfileAccess(client: SupabaseClient, profileId: string) {
  const { error } = await client.rpc("remove_finance_user", {
    p_profile_id: profileId,
  });
  if (error) throw error;
}

export async function loadFinancialAccounts(client: SupabaseClient): Promise<FinancialAccount[]> {
  const { data, error } = await client
    .from("financial_accounts")
    .select("slug,name_id,name_en,section,display_order,is_active,is_system")
    .order("display_order", { ascending: true });
  if (isMissingProfitLossSchema(error)) return [];
  if (error) throw error;
  return ((data || []) as FinancialAccountRow[]).map((row) => ({
    slug: row.slug,
    nameId: row.name_id,
    nameEn: row.name_en,
    section: row.section,
    displayOrder: Number(row.display_order),
    active: row.is_active,
    system: row.is_system,
  }));
}

export async function loadFinancialCodeRules(client: SupabaseClient): Promise<FinancialCodeRule[]> {
  const { data, error } = await client
    .from("financial_code_rules")
    .select("unique_code,direction,financial_account_slug,business_unit_slug,created_by,created_at,updated_at")
    .order("unique_code", { ascending: true })
    .order("direction", { ascending: true });
  if (isMissingProfitLossSchema(error)) return [];
  if (error) throw error;
  return ((data || []) as FinancialCodeRuleRow[]).map((row) => ({
    uniqueCode: row.unique_code,
    direction: row.direction.toUpperCase() as Transaction["direction"],
    financialAccount: row.financial_account_slug,
    businessUnit: row.business_unit_slug,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

export async function saveFinancialCodeRule(
  client: SupabaseClient,
  rule: Pick<FinancialCodeRule, "uniqueCode" | "direction" | "financialAccount" | "businessUnit">,
  applyHistorical: boolean,
) {
  const { data, error } = await client.rpc("upsert_financial_code_rule", {
    p_unique_code: rule.uniqueCode,
    p_direction: rule.direction.toLowerCase(),
    p_financial_account_slug: rule.financialAccount,
    p_business_unit_slug: rule.businessUnit || null,
    p_apply_historical: applyHistorical,
  });
  if (error) throw error;
  return Number((data as Record<string, unknown> | null)?.affected_count || 0);
}

export async function removeFinancialCodeRule(
  client: SupabaseClient,
  uniqueCode: string,
  direction: Transaction["direction"],
) {
  const { error } = await client.rpc("delete_financial_code_rule", {
    p_unique_code: uniqueCode,
    p_direction: direction.toLowerCase(),
  });
  if (error) throw error;
}

export async function saveFinancialAccount(client: SupabaseClient, account: FinancialAccount) {
  const { error } = await client.rpc("upsert_financial_account", {
    p_slug: account.slug,
    p_name_id: account.nameId,
    p_name_en: account.nameEn,
    p_section: account.section,
    p_display_order: account.displayOrder,
    p_is_active: account.active,
  });
  if (error) throw error;
}

export async function saveUniqueCodeCategoryRule(
  client: SupabaseClient,
  uniqueCode: string,
  direction: Transaction["direction"],
  category: string,
  brandContext: string,
  automatic = false,
  applyHistorical = false,
) {
  const { data, error } = await client.rpc("upsert_contextual_code_category_rule", {
    p_unique_code: uniqueCode,
    p_direction: direction.toLowerCase(),
    p_category_slug: category,
    p_brand_context: brandContext,
    p_automatic: automatic,
    p_apply_historical: applyHistorical,
  });
  if (error) throw error;
  return Number((data as Record<string, unknown> | null)?.affected_count || 0);
}

export async function removeUniqueCodeCategoryRule(
  client: SupabaseClient,
  uniqueCode: string,
  direction: Transaction["direction"],
  brandContext: string,
) {
  const { data, error } = await client.rpc("delete_contextual_code_category_rule", {
    p_brand_context: brandContext,
    p_unique_code: uniqueCode,
    p_direction: direction.toLowerCase(),
  });
  if (error) throw error;
  return Number((data as Record<string, unknown> | null)?.affected_count || 0);
}

async function loadSourceLinks(client: SupabaseClient) {
  const pageSize = 1_000;
  const links: SourceLinkRow[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from("transaction_sources")
      .select("transaction_id,import_batch_id")
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    links.push(...((data || []) as SourceLinkRow[]));
    if (!data || data.length < pageSize) break;
  }
  return links;
}

export async function loadRemoteTransactions(client: SupabaseClient, knownSources?: ImportSource[], includeSourceLinks = true) {
  const pageSize = 1_000;
  const rows: TransactionRow[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from("transactions")
      .select("*,ft_number")
      .is("deleted_at", null)
      .order("transaction_timestamp", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...((data || []) as TransactionRow[]));
    if (!data || data.length < pageSize) break;
  }
  const [sources, links] = await Promise.all([
    knownSources ? Promise.resolve(knownSources) : loadRemoteSources(client),
    includeSourceLinks ? loadSourceLinks(client) : Promise.resolve([]),
  ]);
  return applySourceLinks(rows.map(fromRow), sources, links);
}

export async function loadRemoteSourceRows(client: SupabaseClient, sourceId: string) {
  const pageSize = 1_000;
  const rows: ImportSourceRowDetail[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client
      .from("transaction_sources")
      .select("source_row_number,transaction_id,source_payload")
      .eq("import_batch_id", sourceId)
      .order("source_row_number", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    rows.push(...(data || []).map((row) => ({
      rowNumber: Number(row.source_row_number),
      transactionId: String(row.transaction_id),
      sourcePayload: (row.source_payload || {}) as Record<string, unknown>,
    })));
    if (!data || data.length < pageSize) break;
  }
  return rows;
}

export async function updateRemoteTransaction(client: SupabaseClient, id: string, values: { category?: string; subcategory?: string | null; businessUnit?: string; financialAccount?: string | null; automaticFinancialAccount?: boolean; internalDescription?: string | null }) {
  const update: Record<string, string | null> = {};
  if (values.category) {
    update.category_slug = values.category;
    update.subcategory_slug = values.subcategory || null;
    update.category_assignment_source = "manual";
  }
  if (values.subcategory !== undefined) { update.subcategory_slug = values.subcategory || null; update.category_assignment_source = "manual"; }
  if (values.businessUnit) {
    const brands = values.businessUnit.split("+").filter((value) => value !== "unassigned");
    update.business_unit_slug = brands.length === 1 ? brands[0] : "unassigned";
    (update as Record<string, unknown>).business_unit_slugs = brands;
    update.business_unit_mapping_source = "manual";
  }
  if (values.automaticFinancialAccount) {
    update.financial_account_slug = null;
    update.financial_mapping_source = null;
  } else if (values.financialAccount !== undefined) {
    update.financial_account_slug = values.financialAccount;
    update.financial_mapping_source = "manual";
  }
  if (values.internalDescription !== undefined) update.internal_description = values.internalDescription?.trim() || null;
  const { data, error } = await client.from("transactions").update(update).eq("id", id).select("*").single();
  if (error) throw error;
  return data ? fromRow(data as TransactionRow) : undefined;
}

export async function completeRemoteTransactionData(client: SupabaseClient, transaction: Transaction) {
  const { error } = await client.rpc("update_transaction_observation", {
    p_transaction_id: transaction.id,
    p_values: {
      transaction_date: transaction.date || null,
      transaction_timestamp: transaction.transactionTimestamp || null,
      payment_ref: transaction.paymentRef || null,
      source_name: transaction.sourceName || null,
      source_bank: transaction.sourceBank || null,
      beneficiary_name: transaction.beneficiaryName || null,
      beneficiary_bank: transaction.beneficiaryBank || null,
      subject: transaction.subject || "",
      balance_idr: transaction.balance === undefined ? null : Math.trunc(transaction.balance),
      status: transaction.status ? transaction.status.toLowerCase() : null,
    },
    p_confirmed_empty_fields: transaction.confirmedEmptyFields,
  });
  if (error) throw error;
}

export async function completeRemoteTransactionDataBatch(client: SupabaseClient, transactions: Transaction[]) {
  if (!transactions.length) return;
  const { error } = await client.rpc("update_transaction_observations", {
    p_updates: transactions.map((transaction) => ({
      transaction_id: transaction.id,
      values: {
        transaction_date: transaction.date || null,
        transaction_timestamp: transaction.transactionTimestamp || null,
        payment_ref: transaction.paymentRef || null,
        source_name: transaction.sourceName || null,
        source_bank: transaction.sourceBank || null,
        beneficiary_name: transaction.beneficiaryName || null,
        beneficiary_bank: transaction.beneficiaryBank || null,
        subject: transaction.subject || "",
        balance_idr: transaction.balance === undefined ? null : Math.trunc(transaction.balance),
        status: transaction.status ? transaction.status.toLowerCase() : null,
      },
      confirmed_empty_fields: transaction.confirmedEmptyFields,
    })),
  });
  if (error) throw error;
}

export type ManualTransactionResult = {
  action: "created" | "merged";
  transaction_id: string;
  manual_entry_id: string;
};

function manualTransactionPayload(item: Transaction) {
  return {
    payment_ref: item.paymentRef,
    ft_number: item.ftNumber?.trim() || null,
    source_account: item.sourceAccount,
    beneficiary_account: item.beneficiaryAccount,
    amount_idr: Math.trunc(item.amount),
    transaction_date: item.date,
    transaction_timestamp: item.transactionTimestamp || null,
    subject: item.subject,
    internal_description: item.internalDescription?.trim() || null,
    source_name: item.sourceName || null,
    beneficiary_name: item.beneficiaryName || null,
    source_bank: item.sourceBank || null,
    beneficiary_bank: item.beneficiaryBank || null,
    balance_idr: item.balance === undefined ? null : Math.trunc(item.balance),
    transfer_type: item.transferType || null,
    status: item.status.toLowerCase(),
    direction: item.direction.toLowerCase(),
    category_slug: item.category,
    subcategory_slug: item.subcategory || null,
    category_assignment_source: item.categoryAssignmentSource || "unassigned",
    business_unit_slug: item.businessUnit,
      business_unit_slugs: item.businessUnits ?? (item.businessUnit === "unassigned" ? [] : [item.businessUnit]),
    business_unit_mapping_source: item.businessUnitMappingSource || null,
    financial_account_slug: item.financialAccount || null,
    financial_mapping_source: item.financialMappingSource || null,
    account_label: item.accountLabel,
    fingerprint: item.fingerprint,
    identity_keys: item.identityKeys,
    confirmed_empty_fields: item.confirmedEmptyFields,
  };
}

export async function createManualTransaction(
  client: SupabaseClient,
  transaction: Transaction,
  mergeTransactionId: string | null = null,
) {
  const { data, error } = await client.rpc("create_manual_transaction", {
    p_transaction: manualTransactionPayload(transaction),
    p_duplicate_action: mergeTransactionId ? "merge" : "new",
    p_merge_transaction_id: mergeTransactionId,
  });
  if (error) throw error;
  return data as ManualTransactionResult;
}

export async function sha256(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

export function safeStorageFilename(filename: string) {
  const cleaned = filename.normalize("NFKD")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/\.\.+/g, ".")
    .replace(/^[.-]+/g, "")
    .replace(/^-+|-+$/g, "")
    .slice(-140);
  return cleaned || "statement-file";
}

function importPayload(rows: Transaction[]) {
  const occurrences = new Map<string, number>();
  return rows.map((item, index) => {
    const occurrence = (occurrences.get(item.fingerprint) || 0) + 1;
    occurrences.set(item.fingerprint, occurrence);
    return {
      row_number: item.sourceRowNumber || index + 1,
      occurrence,
      payment_ref: item.paymentRef,
      ft_number: item.ftNumber?.trim() || null,
      source_account: item.sourceAccount,
      beneficiary_account: item.beneficiaryAccount,
      amount_idr: item.amount,
      transaction_date: item.date,
      transaction_timestamp: item.transactionTimestamp || null,
      subject: item.subject,
      source_name: item.sourceName || null,
      beneficiary_name: item.beneficiaryName || null,
      source_bank: item.sourceBank || null,
      beneficiary_bank: item.beneficiaryBank || null,
      balance_idr: item.balance === undefined ? null : Math.trunc(item.balance),
      unique_code: item.uniqueCode,
      transfer_type: item.transferType || null,
      status: item.status.toLowerCase(),
      direction: item.direction.toLowerCase(),
      category_slug: item.category,
    subcategory_slug: item.subcategory || null,
      category_assignment_source: item.categoryAssignmentSource || "unassigned",
      business_unit_slug: item.businessUnit,
      business_unit_slugs: item.businessUnits ?? (item.businessUnit === "unassigned" ? [] : [item.businessUnit]),
      business_unit_mapping_source: item.businessUnitMappingSource || null,
      financial_account_slug: item.financialAccount || null,
      financial_mapping_source: item.financialMappingSource || null,
      internal_description: item.internalDescription || null,
      account_label: item.accountLabel,
      fingerprint: item.fingerprint,
      identity_keys: item.identityKeys,
      confirmed_empty_fields: item.confirmedEmptyFields,
      duplicate_policy: "skip_exact_six_v0196",
      source_payload: item,
    };
  });
}

export type RemoteImportResult = {
  batch_id: string;
  row_count: number;
  detected_row_count?: number | null;
  excluded_count?: number | null;
  inserted_count: number;
  duplicate_count: number;
  already_imported: boolean;
  recovered?: boolean;
  reviewed_merge_count?: number;
  skipped_count?: number;
  duplicate_policy?: string;
  data_quality_policy?: string;
  original_file_available?: boolean;
};

export async function recoverLegacyImport(client: SupabaseClient, file: File, requestId: string): Promise<RemoteImportResult | null> {
  const hash = await sha256(file);
  const { data, error } = await client.rpc("import_outcome_v0196", { p_batch_id: requestId, p_file_sha256: hash });
  if (error) throw error;
  if (!data) return null;
  if (data.duplicate_policy !== "skip_exact_six_v0196") throw new Error("MIGRATION_V0196_REQUIRED");
  return { ...data, recovered: true } as RemoteImportResult;
}

export class ImportRejectedError extends Error {}

// One request id survives a failed save and draft recovery. A new import of the
// same bytes is evaluated by all six fields, rather than rejected by file hash.
export async function importRemoteTransactions(
  client: SupabaseClient,
  file: File,
  rows: Transaction[],
  review: { detectedCount: number; excluded: { sourceRowNumber: number; row: Transaction }[]; requestId?: string } = { detectedCount: rows.length, excluded: [] },
  onProgress?: (stage: "preparing" | "uploading" | "saving" | "verifying") => void,
  beforeSave?: () => Promise<ImportSubmission | void>,
) {
  if (rows.some((row) => !Number.isSafeInteger(row.amount) || row.amount <= 0))
    throw new Error("An exact positive integer IDR amount is required");
  onProgress?.("preparing");
  const batchId = review.requestId || crypto.randomUUID();
  const hash = await sha256(file);
  const { data: authData, error: authError } = await client.auth.getUser();
  if (authError || !authData.user) throw authError || new Error("AUTH_USER_UNAVAILABLE");
  const storagePath = `${authData.user.id}/${batchId}/${safeStorageFilename(file.name)}`;
  const bucket = client.storage.from(IMPORT_BUCKET);
  onProgress?.("uploading");
  const upload = await bucket.upload(storagePath, file, { contentType: file.type || "application/octet-stream", upsert: false });
  // The same request may already have uploaded its immutable original file.
  if (upload.error && String((upload.error as { statusCode?: string }).statusCode) !== "409") throw upload.error;
  // Freeze the submitted review durably before a database request can commit.
  // Upload/auth failures leave the review editable.
  const secured = await beforeSave?.();
  if (secured && secured.requestId !== batchId) throw new Error("Import draft identity changed");
  const submittedRows = secured?.rows || rows, submittedReview = secured || review;
  onProgress?.("saving");
  const response = await client.rpc("import_transactions_v0196", {
    p_source_filename: file.name,
    p_source_format: file.name.split(".").pop()?.toLowerCase() || "unknown",
    p_file_sha256: hash,
    p_rows: importPayload(submittedRows),
    p_review: {
      detected_count: submittedReview.detectedCount,
      exclusions: submittedReview.excluded.map((item) => ({ source_row_number: item.sourceRowNumber,
        reason: "owner_excluded", source_payload: item.row.originalPayload || {}, normalized_payload: item.row })),
    },
    p_original_file: { batch_id: batchId, storage_path: storagePath, size_bytes: file.size,
      mime_type: file.type || "application/octet-stream" },
  });
  if (response.error) {
    // Never roll back a possibly committed batch: another import may already
    // have skipped it. Only recover a result; retain the draft on uncertainty.
    onProgress?.("verifying");
    const recovery = await client.rpc("import_outcome_v0196", { p_batch_id: batchId, p_file_sha256: hash });
    if (!recovery.error && recovery.data) return { ...(recovery.data as RemoteImportResult), recovered: true };
    const code = String((response.error as { code?: string }).code || "");
    if (!recovery.error || code === "PGRST202") await bucket.remove([storagePath]);
    // A database validation error rolls the RPC back. The existing receipt
    // check also takes the import lock and has confirmed no saved result.
    if (!recovery.error && /^(?:22|23)[0-9A-Z]{3}$|^P0001$/.test(code))
      throw new ImportRejectedError(response.error.message || "Import rejected");
    throw response.error;
  }
  const result = response.data as RemoteImportResult;
  if (result?.duplicate_policy !== "skip_exact_six_v0196") throw new Error("MIGRATION_V0196_REQUIRED");
  return { ...result, original_file_available: true };
}

function deletionPreviewFromRpc(data: Record<string, unknown>): SourceDeletionPreview {
  return {
    batchCount: Number(data.batch_count || 0),
    transactionCount: Number(data.transaction_count || 0),
    exclusiveTransactionCount: Number(data.exclusive_transaction_count || 0),
    sharedTransactionCount: Number(data.shared_transaction_count || 0),
    originalFileCount: Number(data.original_file_count || 0),
    storagePaths: Array.isArray(data.storage_paths)
      ? data.storage_paths.filter((value): value is string => typeof value === "string")
      : [],
  };
}

export async function previewImportSourceDeletion(client: SupabaseClient, sourceIds: string[]) {
  const { data, error } = await client.rpc("preview_import_source_deletion", {
    p_batch_ids: sourceIds,
  });
  if (error) throw error;
  return deletionPreviewFromRpc((data || {}) as Record<string, unknown>);
}

export async function deleteImportSources(client: SupabaseClient, sourceIds: string[], deleteExclusiveTransactions: boolean) {
  const { data, error } = await client.rpc("delete_import_sources", {
    p_batch_ids: sourceIds,
    p_delete_exclusive_transactions: deleteExclusiveTransactions,
  });
  if (error) throw error;
  const result = (data || {}) as Record<string, unknown>;
  const storagePaths = Array.isArray(result.storage_paths)
    ? result.storage_paths.filter((value): value is string => typeof value === "string")
    : [];
  let storageCleanupFailed = false;
  if (storagePaths.length) {
    const removed = await client.storage.from(IMPORT_BUCKET).remove(storagePaths);
    storageCleanupFailed = Boolean(removed.error);
  }
  return {
    deletedBatchCount: Number(result.deleted_batch_count || 0),
    deletedTransactions: Number(result.deleted_transactions || 0),
    keptTransactions: Number(result.kept_transactions || 0),
    storageCleanupFailed,
  } satisfies SourceDeletionResult;
}

export async function downloadImportSource(client: SupabaseClient, source: ImportSource) {
  if (!source.storagePath) throw new Error("ORIGINAL_FILE_UNAVAILABLE");
  const { data, error } = await client.storage
    .from(source.storageBucket || IMPORT_BUCKET)
    .download(source.storagePath);
  if (error) throw error;
  return data;
}

export async function editRemoteTransaction(client: SupabaseClient, transaction: Transaction, expectedUpdatedAt: string | null, mergeTarget: string | null = null) {
  const { data, error } = await client.rpc("edit_finance_transaction", {
    p_transaction_id: transaction.id, p_values: manualTransactionPayload(transaction),
    p_expected_updated_at: expectedUpdatedAt, p_merge_target_id: mergeTarget,
  });
  if (error) throw error;
  return fromRow((data as { transaction: TransactionRow }).transaction);
}

export async function setTransactionDeleted(client: SupabaseClient, transaction: Transaction, deleted: boolean) {
  const { data, error } = await client.rpc("set_finance_transaction_deleted", { p_transaction_id: transaction.id, p_deleted: deleted, p_expected_updated_at: transaction.updatedAt || null });
  if (error) throw error;
  return fromRow((data as { transaction: TransactionRow }).transaction);
}

export async function loadDeletedTransactions(client: SupabaseClient, sources: ImportSource[]) {
  const { data, error } = await client.rpc("list_deleted_finance_transactions");
  if (error) throw error;
  return ((data || []) as TransactionRow[]).map(fromRow).map((row) => {
    const links = (data as Record<string, unknown>[]).find((value) => value.id === row.id)?.source_batch_ids;
    const ids = Array.isArray(links) ? links as string[] : [];
    return { ...row, sourceBatchIds: ids, sourceFiles: ids.map((id) => sources.find((source) => source.id === id)?.fileName || row.sourceFile) };
  });
}

export async function loadDescriptionRules(client: SupabaseClient): Promise<DescriptionRule[]> {
  const { data, error } = await client.from("description_category_rules").select("*").order("priority", { ascending: false }).order("id");
  if (error) throw error;
  return (data || []).map((r) => ({ id: r.id, name: r.name, pattern: r.pattern, field: r.match_field === "internal_description" ? "internalDescription" : r.match_field,
    match: r.match_mode, direction: r.direction.toUpperCase(), brandContext: r.brand_context, category: r.category_slug, subcategory: r.subcategory_slug || undefined,
    priority: r.priority, active: r.is_active, targetBrandContext: r.target_brand_context ?? null }));
}
export async function saveDescriptionRule(client: SupabaseClient, rule: DescriptionRule) {
  const { error } = await client.rpc("save_description_category_rule", { p_rule: { id: rule.id, name: rule.name, pattern: rule.pattern,
    match_field: rule.field === "internalDescription" ? "internal_description" : rule.field, match_mode: rule.match,
    direction: rule.direction.toLowerCase(), brand_context: rule.brandContext, target_brand_context: rule.targetBrandContext ?? null, category_slug: rule.category, subcategory_slug: rule.subcategory || null, priority: rule.priority, is_active: rule.active } });
  if (error) throw error;
}
export async function deleteDescriptionRule(client: SupabaseClient, id: string) {
  const { error } = await client.rpc("delete_description_category_rule", { p_id: id });
  if (error) throw error;
}

function bulkRpcArguments(request: BulkRequest) {
  return { p_items: request.items, p_mode: request.mode, p_patch: request.patch, p_rule_filter: request.ruleFilter };
}
export async function previewRemoteBulk(client: SupabaseClient, request: BulkRequest, originals: Transaction[]): Promise<BulkPreview> {
  const { data, error } = await client.rpc('finance_bulk_transactions_v0170', bulkRpcArguments(request));
  if (error) throw error;
  const result = data as { digest: string; plans: { id: string; status: BulkStatus; before?: TransactionRow; after?: TransactionRow; message?: string }[] };
  if (!result?.digest || !Array.isArray(result.plans)) throw new Error('Invalid bulk preview response');
  const originalById = new Map(originals.map((r) => [r.id,r]));
  const convert = (value?: TransactionRow) => {
    if (!value) return undefined;
    const row = fromRow(value), original = originalById.get(row.id);
    return original ? { ...row, sourceBatchIds: original.sourceBatchIds, sourceFiles: original.sourceFiles, sourceFile: original.sourceFile, sourceRowNumber: original.sourceRowNumber } : row;
  };
  return { digest: result.digest, request, plans: result.plans.map((p) => ({ ...p, before: convert(p.before), after: convert(p.after) })) };
}
export async function commitRemoteBulk(client: SupabaseClient, preview: BulkPreview): Promise<Transaction[]> {
  const { data, error } = await client.rpc('finance_bulk_transactions_v0170', { ...bulkRpcArguments(preview.request), p_preview_digest: preview.digest });
  if (error) throw error;
  const result = data as { committed: boolean; transactions: TransactionRow[] };
  if (!result?.committed || !Array.isArray(result.transactions)) throw new Error('Invalid bulk save response');
  return result.transactions.map(fromRow);
}

export async function loadCategoryFinancialMappings(client:SupabaseClient):Promise<{mappings:CategoryFinancialMapping[];available:boolean}>{
  const {data,error}=await client.from('category_financial_mappings').select('category_slug,financial_account_slug,is_active,updated_at').order('category_slug');
  if(error && ['42P01','PGRST205'].includes(error.code))return {mappings:[],available:false};
  if(error)throw error;
  return {available:true,mappings:(data||[]).map((r)=>({category:r.category_slug,financialAccount:r.financial_account_slug,active:r.is_active,updatedAt:r.updated_at}))};
}
export async function saveCategoryFinancialMapping(client:SupabaseClient,mapping:CategoryFinancialMapping,category:FinanceCategory,account:FinancialAccount){
  const {data,error}=await client.rpc('save_category_financial_mapping_v0180',{p_category_slug:mapping.category,p_financial_account_slug:mapping.financialAccount,p_is_active:mapping.active,p_expected_updated_at:mapping.updatedAt||null,p_expected_category_kind:category.kind,p_expected_account_section:account.section});
  if(error)throw error;
  const r=data as Record<string,unknown>;
  return {category:String(r.category_slug),financialAccount:String(r.financial_account_slug),active:Boolean(r.is_active),updatedAt:String(r.updated_at)} satisfies CategoryFinancialMapping;
}
export async function removeCategoryFinancialMapping(client:SupabaseClient,mapping:CategoryFinancialMapping){
  const {error}=await client.rpc('delete_category_financial_mapping_v0180',{p_category_slug:mapping.category,p_expected_updated_at:mapping.updatedAt||null});if(error)throw error;
}
export async function removeFinanceDefinition(client:SupabaseClient,kind:'category'|'account'|'brand',value:FinanceCategory|FinancialAccount|BusinessUnit){
  let expected:Record<string,unknown>;
  if(kind==='category'){const c=value as FinanceCategory;expected={slug:c.slug,name_id:c.nameId,name_en:c.nameEn,kind:c.kind,parent_slug:c.parentSlug||null,is_active:c.active,color:c.color,display_order:c.displayOrder};}
  else if(kind==='account'){const a=value as FinancialAccount;expected={slug:a.slug,name_id:a.nameId,name_en:a.nameEn,section:a.section,is_active:a.active,display_order:a.displayOrder};}
  else {const b=value as BusinessUnit;expected={slug:b.slug,name:b.name,short_name:b.shortName,color:b.color,is_active:b.active,display_order:b.displayOrder};}
  const {error}=await client.rpc('delete_finance_definition_v0180',{p_kind:kind,p_slug:value.slug,p_expected:expected});if(error)throw error;
}

export async function loadSimpleProfitRules(client:SupabaseClient):Promise<{rules:import('./simple-profit-loss').SimpleProfitRule[];available:boolean}>{
  const {data,error}=await client.from('simple_profit_treatments').select('category_slug,transaction_id,treatment,updated_at');
  if(error && ['42P01','PGRST205'].includes(error.code))return {rules:[],available:false};
  if(error)throw error;
  return {available:true,rules:(data||[]).map(r=>({kind:r.transaction_id?'transaction':'category',entityId:r.transaction_id||r.category_slug,treatment:r.treatment,updatedAt:r.updated_at}))};
}
export async function saveSimpleProfitTreatment(client:SupabaseClient,kind:import('./simple-profit-loss').SimpleProfitRule['kind'],entity:Transaction|FinanceCategory,treatment:import('./simple-profit-loss').SimpleProfitTreatment|null,current?:import('./simple-profit-loss').SimpleProfitRule){
  const entityId=kind==='transaction'?(entity as Transaction).id:(entity as FinanceCategory).slug;
  const expected=kind==='transaction'?{updated_at:(entity as Transaction).updatedAt||null}:{slug:(entity as FinanceCategory).slug,kind:(entity as FinanceCategory).kind,name_id:(entity as FinanceCategory).nameId,parent_slug:(entity as FinanceCategory).parentSlug||null};
  const {data,error}=await client.rpc('save_simple_profit_treatment_v0190',{p_kind:kind,p_entity_id:entityId,p_treatment:treatment,p_expected_updated_at:current?.updatedAt||null,p_expected_entity:expected});
  if(error)throw error;if(!data)return null;
  return {kind,entityId,treatment:data.treatment,updatedAt:data.updated_at} as import('./simple-profit-loss').SimpleProfitRule;
}

export async function loadRolePolicies(client: SupabaseClient): Promise<RolePolicy[]> {
  const {data,error}=await client.from("finance_role_policies").select("role,permissions,updated_at").order("role");
  if(error)throw error;
  return (data||[]).map(row=>({role:row.role as ReadRole,permissions:parsePermissions(row.permissions),updatedAt:row.updated_at}));
}
export async function saveRolePolicy(client: SupabaseClient, policy: RolePolicy): Promise<RolePolicy> {
  const {data,error}=await client.rpc("save_finance_role_policy_v0197",{p_role:policy.role,p_permissions:policy.permissions,p_expected_updated_at:policy.updatedAt});
  if(error)throw error;
  return {role:data.role,permissions:parsePermissions(data.permissions),updatedAt:data.updated_at};
}
